import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import {
    LineChart, Line, Bar, AreaChart, Area, ComposedChart,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    ReferenceLine, Legend,
} from 'recharts';
import { useBankLens } from './BankLensContext';
import {
    BENCHMARKS, METRIC_INFO,
} from './banksData';
import styles from './BankLensPage.module.css';

/* ── Tiny sparkline for metric cards ──────────────────────── */
function Sparkline({ data, dataKey, color, height = 36 }) {
    return (
        <div className={styles.metricSparkline}>
            <ResponsiveContainer width="100%" height={height}>
                <LineChart data={data}>
                    <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={1.5} dot={false} />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}

/* ── Info tooltip button ──────────────────────────────────── */
function InfoBtn({ metricKey }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const toggle = useCallback((e) => {
        e.stopPropagation();
        setOpen(o => !o);
    }, []);

    const info = METRIC_INFO[metricKey];
    if (!info) return null;

    return (
        <>
            <button ref={ref} className={styles.infoBtn} onClick={toggle} title={info.title}>i</button>
            {open && (
                <>
                    <div className={styles.tooltipOverlay} onClick={() => setOpen(false)} />
                    <div className={styles.tooltipCard} style={{
                        top: ref.current ? ref.current.getBoundingClientRect().bottom + 6 : 0,
                        left: ref.current ? ref.current.getBoundingClientRect().left : 0,
                    }}>
                        <div className={styles.tooltipTitle}>{info.title}</div>
                        <div className={styles.tooltipFormula}>{info.formula}</div>
                        <p className={styles.tooltipDesc}>{info.desc}</p>
                    </div>
                </>
            )}
        </>
    );
}

/* ── Chart tooltip ────────────────────────────────────────── */
function ChartTip({ active, payload, label, suffix = '' }) {
    if (!active || !payload?.length) return null;
    return (
        <div className={styles.rechartsTip}>
            <div className={styles.rechartsTipLabel}>{label}</div>
            {payload.map((p, i) => (
                <div key={i} className={styles.rechartsTipRow}>
                    <span className={styles.rechartsTipDot} style={{ background: p.color }} />
                    <span className={styles.rechartsTipName}>{p.name}</span>
                    <span className={styles.rechartsTipVal}>{p.value != null ? `${typeof p.value === 'number' ? p.value.toLocaleString('en-IN') : p.value}${suffix}` : '—'}</span>
                </div>
            ))}
        </div>
    );
}

/* ── Main component ───────────────────────────────────────── */
export default function BankLensDetail({ ticker, onBack }) {
    const { banks: BANKS, getLatestMetrics, getMetrics, getLiquidityData, fmtMktCap, fmtCr, getStatus, loadDetail } = useBankLens();

    /* Eagerly fetch bank detail (populates history cache in context) */
    useEffect(() => { loadDetail(ticker); }, [ticker, loadDetail]);

    const bank = BANKS.find(b => b.ticker === ticker);
    const latest = useMemo(() => getLatestMetrics(ticker), [ticker, getLatestMetrics]);
    const metrics = useMemo(() => getMetrics(ticker), [ticker, getMetrics]);
    const liqData = useMemo(() => getLiquidityData(ticker), [ticker, getLiquidityData]);

    const chartData = useMemo(() =>
        metrics.map(d => ({ ...d, year: `FY${d.y}` })),
        [metrics]);

    // Previous year data for YoY
    const prev = metrics.length >= 2 ? metrics[metrics.length - 2] : null;
    const yoy = (cur, old) => {
        if (cur == null || old == null || old === 0) return null;
        return ((cur - old) / Math.abs(old) * 100).toFixed(1);
    };

    if (!bank) return <div className={styles.main}>Bank not found</div>;

    const statusClass = (key) => {
        const s = getStatus(latest[key], key);
        return s === 'good' ? styles.statusGood : s === 'ok' ? styles.statusOk : s === 'bad' ? styles.statusBad : '';
    };

    const dotClass = (key) => {
        const s = getStatus(latest[key], key);
        return s === 'good' ? styles.bgGood : s === 'ok' ? styles.bgOk : s === 'bad' ? styles.bgBad : styles.bgNeutral;
    };

    return (
        <>
            {/* ── Back ──── */}
            <button className={styles.backBtn} onClick={onBack}>
                ← Back to Overview
            </button>

            {/* ── Hero ──── */}
            <div className={styles.detailHero} style={{ borderLeft: `4px solid ${bank.color}` }}>
                <div className={styles.heroLeft}>
                    <h1 className={styles.heroName}>
                        {bank.name}
                        <span className={styles.heroTicker}>{bank.ticker.replace('.NS', '')}</span>
                    </h1>
                    <p className={styles.heroSub}>{bank.type === 'PVT' ? 'Private Sector' : 'Public Sector'} Bank · NSE</p>
                    <div>
                        <span className={styles.heroPrice}>
                            ₹{latest.price?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </span>
                        <span className={`${styles.heroChange} ${latest.changePct >= 0 ? styles.positive : styles.negative}`}>
                            {latest.changePct >= 0 ? '▲' : '▼'} ₹{Math.abs(latest.change).toFixed(2)} ({Math.abs(latest.changePct).toFixed(2)}%)
                        </span>
                    </div>
                </div>
                <div className={styles.heroRight}>
                    <div className={styles.heroStat}>
                        <div className={styles.heroStatLabel}>Market Cap</div>
                        <div className={styles.heroStatValue}>{fmtMktCap(latest.marketCap)}</div>
                    </div>
                    <div className={styles.heroStat}>
                        <div className={styles.heroStatLabel}>P/E</div>
                        <div className={styles.heroStatValue}>{latest.pe?.toFixed(1)}x</div>
                    </div>
                    <div className={styles.heroStat}>
                        <div className={styles.heroStatLabel}>EPS</div>
                        <div className={styles.heroStatValue}>₹{latest.eps?.toFixed(2)}</div>
                    </div>
                    <div className={styles.heroStat}>
                        <div className={styles.heroStatLabel}>52W Range</div>
                        <div className={styles.heroStatValue}>₹{latest.yearLow} – ₹{latest.yearHigh}</div>
                    </div>
                </div>
            </div>

            {/* ═══ PROFITABILITY ═══ */}
            <h2 className={styles.sectionTitle}>
                <span className={styles.sectionIcon}>📈</span>Profitability Metrics
            </h2>

            <div className={styles.metricsRow}>
                {[
                    { key: 'nim', label: 'NIM', val: latest.nim, fmt: v => v?.toFixed(2) + '%', sub: `Spread: ${(latest.nim - 0.5).toFixed(1)}% above cost` },
                    { key: 'roa', label: 'ROA', val: latest.roa, fmt: v => v?.toFixed(2) + '%', sub: `Assets: ${fmtCr(latest.ta)}` },
                    { key: 'roe', label: 'ROE', val: latest.roe, fmt: v => v?.toFixed(1) + '%', sub: `Equity: ${fmtCr(latest.eq)}` },
                    { key: 'costToIncome', label: 'Cost/Income', val: latest.cti, fmt: v => v?.toFixed(1) + '%', sub: BENCHMARKS.costToIncome.label },
                ].map(m => {
                    const change = yoy(m.val, prev?.[m.key === 'costToIncome' ? 'cti' : m.key]);
                    return (
                        <div key={m.key} className={styles.metricCard}>
                            <div className={styles.metricCardLabel}>
                                <span className={styles.statusDot + ' ' + dotClass(m.key)} />
                                {m.label}
                                <InfoBtn metricKey={m.key} />
                            </div>
                            <div className={`${styles.metricCardValue} ${statusClass(m.key)}`}>
                                {m.fmt(m.val)}
                            </div>
                            <div className={styles.metricCardSub}>{m.sub}</div>
                            {change != null && (
                                <div className={`${styles.metricCardYoy} ${parseFloat(change) >= 0 ? styles.positive : styles.negative}`}>
                                    {parseFloat(change) >= 0 ? '▲' : '▼'} {Math.abs(parseFloat(change))}% YoY
                                </div>
                            )}
                            <Sparkline data={chartData} dataKey={m.key === 'costToIncome' ? 'cti' : m.key} color={bank.color} />
                        </div>
                    );
                })}
            </div>

            <div className={styles.chartsRow}>
                {/* Revenue & Net Income */}
                <div className={styles.chartContainer}>
                    <div className={styles.cardTitle}>Revenue & Net Income</div>
                    <ResponsiveContainer width="100%" height={220}>
                        <ComposedChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="year" tick={{ fontSize: 10, fill: '#7a8698' }} />
                            <YAxis tick={{ fontSize: 10, fill: '#7a8698' }} width={55}
                                tickFormatter={v => v >= 100000 ? `${(v / 100000).toFixed(0)}L` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v} />
                            <Tooltip content={<ChartTip suffix=" Cr" />} />
                            <Bar dataKey="rev" fill="rgba(32,176,114,0.25)" stroke="#20b072" name="Revenue" radius={[3, 3, 0, 0]} />
                            <Line type="monotone" dataKey="pat" stroke="#38bdf8" strokeWidth={2} dot={{ r: 3 }} name="Net Income" />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>

                {/* Interest Income vs Expense */}
                <div className={styles.chartContainer}>
                    <div className={styles.cardTitle}>Interest Income vs Expense</div>
                    <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="year" tick={{ fontSize: 10, fill: '#7a8698' }} />
                            <YAxis tick={{ fontSize: 10, fill: '#7a8698' }} width={55}
                                tickFormatter={v => v >= 100000 ? `${(v / 100000).toFixed(0)}L` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v} />
                            <Tooltip content={<ChartTip suffix=" Cr" />} />
                            <Line type="monotone" dataKey="intInc" stroke="#20b072" strokeWidth={2} dot={{ r: 3 }} name="Interest Income" />
                            <Line type="monotone" dataKey="intExp" stroke="#e04848" strokeWidth={2} dot={{ r: 3 }} name="Interest Expense" />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* ═══ ASSET QUALITY ═══ */}
            <h2 className={styles.sectionTitle}>
                <span className={styles.sectionIcon}>🛡️</span>Asset Quality
            </h2>

            <div className={styles.metricsRow} style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                {[
                    { key: 'gnpa', val: latest.gnpa, fmt: v => v?.toFixed(2) + '%' },
                    { key: 'nnpa', val: latest.nnpa, fmt: v => v?.toFixed(2) + '%' },
                    { key: 'pcr', val: latest.pcr, fmt: v => v + '%' },
                ].map(m => {
                    const change = yoy(m.val, prev?.[m.key]);
                    return (
                        <div key={m.key} className={styles.metricCard}>
                            <div className={styles.metricCardLabel}>
                                <span className={styles.statusDot + ' ' + dotClass(m.key)} />
                                {METRIC_INFO[m.key]?.label}
                                <InfoBtn metricKey={m.key} />
                            </div>
                            <div className={`${styles.metricCardValue} ${statusClass(m.key)}`}>
                                {m.fmt(m.val)}
                            </div>
                            <div className={styles.metricCardSub}>{BENCHMARKS[m.key]?.label}</div>
                            {change != null && (
                                <div className={`${styles.metricCardYoy} ${m.key === 'pcr'
                                    ? (parseFloat(change) >= 0 ? styles.positive : styles.negative)
                                    : (parseFloat(change) <= 0 ? styles.positive : styles.negative)
                                    }`}>
                                    {parseFloat(change) >= 0 ? '▲' : '▼'} {Math.abs(parseFloat(change))}% YoY
                                </div>
                            )}
                            <Sparkline data={chartData} dataKey={m.key} color={bank.color} />
                        </div>
                    );
                })}
            </div>

            <div className={styles.chartsRow}>
                <div className={styles.chartContainer}>
                    <div className={styles.cardTitle}>GNPA & NNPA Trend</div>
                    <ResponsiveContainer width="100%" height={220}>
                        <AreaChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="year" tick={{ fontSize: 10, fill: '#7a8698' }} />
                            <YAxis tick={{ fontSize: 10, fill: '#7a8698' }} width={40} />
                            <Tooltip content={<ChartTip suffix="%" />} />
                            <Area type="monotone" dataKey="gnpa" stroke="#e04848" fill="rgba(224,72,72,0.12)" strokeWidth={2} name="GNPA" />
                            <Area type="monotone" dataKey="nnpa" stroke="#f59e0b" fill="rgba(245,158,11,0.12)" strokeWidth={2} name="NNPA" />
                            <ReferenceLine y={2.5} stroke="#7a8698" strokeDasharray="4 4" label={{ value: '2.5% Threshold', fill: '#7a8698', fontSize: 10, position: 'right' }} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>

                <div className={styles.chartContainer}>
                    <div className={styles.cardTitle}>Provision Coverage Ratio</div>
                    <ResponsiveContainer width="100%" height={220}>
                        <ComposedChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="year" tick={{ fontSize: 10, fill: '#7a8698' }} />
                            <YAxis tick={{ fontSize: 10, fill: '#7a8698' }} width={40} domain={[0, 100]} />
                            <Tooltip content={<ChartTip suffix="%" />} />
                            <Bar dataKey="pcr" fill="rgba(32,176,114,0.3)" stroke="#20b072" name="PCR" radius={[3, 3, 0, 0]} />
                            <ReferenceLine y={70} stroke="#7a8698" strokeDasharray="4 4" label={{ value: '70% Safe', fill: '#7a8698', fontSize: 10, position: 'right' }} />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* ═══ CAPITAL STRENGTH ═══ */}
            <h2 className={styles.sectionTitle}>
                <span className={styles.sectionIcon}>🏦</span>Capital Strength
            </h2>

            <div className={styles.metricsRow} style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                {[
                    { key: 'car', val: latest.car, fmt: v => v?.toFixed(1) + '%' },
                    { key: 'cet1', val: latest.cet1, fmt: v => v?.toFixed(1) + '%' },
                ].map(m => (
                    <div key={m.key} className={styles.metricCard}>
                        <div className={styles.metricCardLabel}>
                            <span className={styles.statusDot + ' ' + dotClass(m.key)} />
                            {METRIC_INFO[m.key]?.label}
                            <InfoBtn metricKey={m.key} />
                        </div>
                        <div className={`${styles.metricCardValue} ${statusClass(m.key)}`}>
                            {m.fmt(m.val)}
                        </div>
                        <div className={styles.metricCardSub}>{BENCHMARKS[m.key]?.label}</div>
                        <Sparkline data={chartData} dataKey={m.key} color={bank.color} />
                    </div>
                ))}
            </div>

            <div className={styles.chartsRow}>
                <div className={styles.chartContainer}>
                    <div className={styles.cardTitle}>CAR & CET1 Trend</div>
                    <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="year" tick={{ fontSize: 10, fill: '#7a8698' }} />
                            <YAxis tick={{ fontSize: 10, fill: '#7a8698' }} width={40} />
                            <Tooltip content={<ChartTip suffix="%" />} />
                            <Line type="monotone" dataKey="car" stroke="#20b072" strokeWidth={2} dot={{ r: 3 }} name="CAR" />
                            <Line type="monotone" dataKey="cet1" stroke="#38bdf8" strokeWidth={2} dot={{ r: 3 }} name="CET1" />
                            <ReferenceLine y={11.5} stroke="#e04848" strokeDasharray="4 4" label={{ value: 'RBI Min 11.5%', fill: '#e04848', fontSize: 10, position: 'left' }} />
                            <ReferenceLine y={8} stroke="#f59e0b" strokeDasharray="4 4" label={{ value: 'CET1 Min 8%', fill: '#f59e0b', fontSize: 10, position: 'left' }} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* ═══ LIQUIDITY ═══ */}
            <h2 className={styles.sectionTitle}>
                <span className={styles.sectionIcon}>💧</span>Liquidity Health
            </h2>

            <div className={styles.metricsRow} style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                <div className={styles.metricCard}>
                    <div className={styles.metricCardLabel}>
                        <span className={styles.statusDot + ' ' + dotClass('ldr')} />
                        LDR (CD Ratio)
                        <InfoBtn metricKey="ldr" />
                    </div>
                    <div className={`${styles.metricCardValue} ${statusClass('ldr')}`}>
                        {latest.ldr?.toFixed(1)}%
                    </div>
                    <div className={styles.metricCardSub}>{BENCHMARKS.ldr.label}</div>
                </div>
                <div className={styles.metricCard}>
                    <div className={styles.metricCardLabel}>CRR <InfoBtn metricKey="crr" /></div>
                    <div className={styles.metricCardValue}>4.0%</div>
                    <div className={styles.metricCardSub}>RBI monetary policy rate</div>
                </div>
                <div className={styles.metricCard}>
                    <div className={styles.metricCardLabel}>SLR <InfoBtn metricKey="slr" /></div>
                    <div className={styles.metricCardValue}>18.0%</div>
                    <div className={styles.metricCardSub}>Statutory liquidity requirement</div>
                </div>
            </div>

            <div className={styles.chartsRow}>
                <div className={styles.chartContainer}>
                    <div className={styles.cardTitle}>Loan-to-Deposit Ratio Trend</div>
                    <ResponsiveContainer width="100%" height={220}>
                        <ComposedChart data={liqData.map(d => ({ ...d, year: `FY${d.year}` }))}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="year" tick={{ fontSize: 10, fill: '#7a8698' }} />
                            <YAxis tick={{ fontSize: 10, fill: '#7a8698' }} width={40} domain={[50, 110]} />
                            <Tooltip content={<ChartTip suffix="%" />} />
                            <Bar dataKey="ldr" fill="rgba(32,176,114,0.25)" stroke="#20b072" name="LDR" radius={[3, 3, 0, 0]} />
                            <ReferenceLine y={85} stroke="#f59e0b" strokeDasharray="4 4" label={{ value: '85% Ideal Cap', fill: '#f59e0b', fontSize: 10, position: 'right' }} />
                            <ReferenceLine y={95} stroke="#e04848" strokeDasharray="4 4" label={{ value: '95% Stress', fill: '#e04848', fontSize: 10, position: 'right' }} />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>

                <div className={styles.chartContainer}>
                    <div className={styles.cardTitle}>CRR & SLR (Policy Rates)</div>
                    <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={liqData.map(d => ({ ...d, year: `FY${d.year}` }))}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="year" tick={{ fontSize: 10, fill: '#7a8698' }} />
                            <YAxis tick={{ fontSize: 10, fill: '#7a8698' }} width={40} domain={[0, 25]} />
                            <Tooltip content={<ChartTip suffix="%" />} />
                            <Line type="stepAfter" dataKey="crr" stroke="#a78bfa" strokeWidth={2} dot={{ r: 3 }} name="CRR" />
                            <Line type="stepAfter" dataKey="slr" stroke="#ec4899" strokeWidth={2} dot={{ r: 3 }} name="SLR" />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </>
    );
}
